/**
 * Utility functions for working with enum objects
 */

import {
  interestCategoryLabel,
  interestLabel,
  pickApiLabel,
  skillCategoryLabel,
  skillLabel,
} from '../i18n/enumLabels';

/**
 * Get label from an enum object by trying multiple possible field names
 * @param enumObject - The enum object (skill, interest, event type, etc.)
 * @param possibleFields - Array of possible field names to check (in order of priority)
 * @param fallback - Fallback value if no field is found
 * @returns The label string or fallback value
 */
export const getEnumLabel = (
  enumObject: any,
  possibleFields: string[],
  fallback: string = ''
): string => {
  if (!enumObject) return fallback;

  for (const field of possibleFields) {
    if (enumObject[field]) {
      return enumObject[field];
    }
  }

  return fallback;
};

/**
 * Get skill label from a skill category or a single skill, in the current language
 * Tries: skill_name, category_name, name
 */
export const getSkillLabel = (skillData: any, fallback: string = ''): string => {
  if (skillData?.skill_id !== undefined) return getNestedSkillName(skillData);

  const apiLabel = getEnumLabel(skillData, ['skill_name', 'category_name', 'name'], fallback);
  return skillData ? skillCategoryLabel(skillData.id, apiLabel) : apiLabel;
};

/**
 * Get interest label from an interest category or a single interest, in the current language
 * Tries: interest_name, category_name, name
 */
export const getInterestLabel = (interestData: any, fallback: string = ''): string => {
  if (interestData?.interest_id !== undefined) return getNestedInterestName(interestData);

  const apiLabel = getEnumLabel(interestData, ['interest_name', 'category_name', 'name'], fallback);
  return interestData ? interestCategoryLabel(interestData.id, apiLabel) : apiLabel;
};

/**
 * Get event type label from event type data object, in the current language
 * Tries: type_name, event_type_name, name
 */
export const getEventTypeLabel = (eventTypeData: any, fallback: string = ''): string => {
  const apiLabel = getEnumLabel(eventTypeData, ['type_name', 'event_type_name', 'name'], fallback);
  return pickApiLabel(apiLabel, eventTypeData?.event_type_name_en);
};

/**
 * Get nested skill item name based on user type, in the current language
 * Tries: skill_name_vol (for volunteer), skill_name_org (for organisation), skill_name, name
 */
export const getNestedSkillName = (skillItem: any, userType: 'volunteer' | 'organisation' = 'volunteer'): string => {
  const fields = userType === 'volunteer' 
    ? ['skill_name_vol', 'skill_name', 'name']
    : ['skill_name_org', 'skill_name', 'name'];
  return skillLabel(skillItem?.skill_id, userType === 'volunteer' ? 'vol' : 'org', getEnumLabel(skillItem, fields));
};

/**
 * Get nested interest item name based on user type, in the current language
 * Tries: interest_name_vol (for volunteer), interest_name_org (for organisation), interest_name, name
 */
export const getNestedInterestName = (interestItem: any, userType: 'volunteer' | 'organisation' = 'volunteer'): string => {
  const fields = userType === 'volunteer' 
    ? ['interest_name_vol', 'interest_name', 'name']
    : ['interest_name_org', 'interest_name', 'name'];
  return interestLabel(interestItem?.interest_id, userType === 'volunteer' ? 'vol' : 'org', getEnumLabel(interestItem, fields));
};
